/**
 * Google Identity Services (GIS) auth helper.
 * Replaces firebase.config.ts — same API surface (onAuthChange, signInWithGoogle,
 * getIdToken, signOut) so existing consumers need only update their import path.
 *
 * GIS script is loaded via <script> in index.html.
 * Env var: VITE_GOOGLE_CLIENT_ID
 *
 * ── FedCM (Federated Credential Management) ──────────────────────────────────
 * As of Aug 2025 FedCM is MANDATORY for GIS: once a browser restricts
 * third-party cookies, the legacy (non-FedCM) One-Tap / account-chooser can no
 * longer render — it shows a dialog that never populates and freezes. Chrome 149
 * dropped the last legacy 3p-cookie fallback, so interactive sign-in only works
 * via FedCM. We therefore enable BOTH FedCM surfaces:
 *   - use_fedcm_for_prompt  → the auto-fired One-Tap (google.accounts.id.prompt)
 *   - use_fedcm_for_button  → the rendered "Sign in with Google" button (the
 *                             control the user actually taps on the login page)
 * Trade-off: FedCM UI cannot be programmatically cancelled, so the old
 * background "silent refresh" (prompt()+cancel()) is gone — the 7-day
 * va_session cookie already carries REST + WebSocket auth, so a stale GSI
 * Bearer token is harmless and re-login happens only after the cookie expires.
 */

declare const google: any;

const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';
const STORAGE_KEY = 'gsi_id_token';

export interface GoogleUser {
  email: string;
  name: string;
  picture?: string;
  sub: string;
}

let _idToken: string | null = null;
let _user: GoogleUser | null = null;
const _listeners = new Set<(user: GoogleUser | null) => void>();
let _gisReady = false;
let _authResolved = false;

// ── JWT decode (no verification — backend verifies) ──────────────────────────

function decodeJwt(token: string): Record<string, any> | null {
  try {
    const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(b64));
  } catch {
    return null;
  }
}

/**
 * No-op under FedCM. Previously attempted a silent GSI token refresh on
 * WebSocket reconnect; FedCM UI can't be silently cancelled, so we rely on the
 * 7-day va_session cookie (the WebSocket cookie fallback) to carry auth. Kept
 * as an exported symbol so existing callers don't need to change.
 */
export function kickGsiRefresh(): void {
  /* intentionally empty — va_session cookie carries auth, see header comment */
}

function setUser(user: GoogleUser | null, token: string | null) {
  _user = user;
  _idToken = token;
  if (token) {
    try { localStorage.setItem(STORAGE_KEY, token); } catch { /* ignore */ }
  } else {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  }
  _listeners.forEach((cb) => cb(user));
}

function handleCredentialResponse(response: { credential: string }) {
  const token = response.credential;
  const payload = decodeJwt(token);
  if (!payload) return;
  // Exchange GSI token for a long-lived session cookie (7 days)
  fetch('/api/me/session', {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
  }).catch(() => {}); // best-effort — cookie is a bonus, Bearer still works
  setUser(
    {
      email: payload['email'] as string,
      name: (payload['name'] as string) || (payload['email'] as string),
      picture: payload['picture'] as string | undefined,
      sub: payload['sub'] as string,
    },
    token,
  );
}

// ── Init (called once from App.tsx) ──────────────────────────────────────────

export function initGoogleAuth() {
  // Restore from localStorage if token is still valid
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const payload = decodeJwt(stored);
      if (payload && payload['exp'] * 1000 > Date.now()) {
        _idToken = stored;
        _user = {
          email: payload['email'] as string,
          name: (payload['name'] as string) || (payload['email'] as string),
          picture: payload['picture'] as string | undefined,
          sub: payload['sub'] as string,
        };
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  } catch { /* ignore */ }

  // ── Two independent jobs, deliberately NOT chained ────────────────────────────────────────────
  // 1) SETTLE the auth state (stored token, else the va_session cookie): needs nothing but our own
  //    backend. 2) BRING UP Google Identity Services for the sign-in button and One-Tap: needs Google's
  //    script from the public internet. They used to be one chain (the cookie restore started inside
  //    tryInit, once Google's script had defined its `id` namespace), so a user with a perfectly valid
  //    va_session cookie on a network that could not reach accounts.google.com got a permanently dead
  //    app: no shell, no login page, an empty <body> (measured in #2164: GSI stalled => the shell never
  //    renders; and by the nightly live-dom sweeps, which share a Wi-Fi uplink). Auth is OUR state;
  //    Google is only how a NEW user proves who they are. Pinned by
  //    setpoint-evals/live-dom-resilience/SE-03.
  let oneTapOffered = false;
  const offerOneTap = () => {
    // Only auto-fire One-Tap on the genuine login screen (no cookie-restored user), and only when BOTH
    // halves are done — auth settled AND GIS initialized — whichever finishes last. For an already-authed
    // user this would pop a FedCM sheet that can't be cancelled. The rendered button (AuthPage) is the
    // primary path. Once: it is reachable from both halves.
    if (oneTapOffered || !_authResolved || !_gisReady || _user) return;
    oneTapOffered = true;
    google.accounts.id.prompt();
  };

  const settleAuth = () => {
    if (_idToken) {
      // Valid stored token — fire listeners immediately
      _authResolved = true;
      _listeners.forEach((cb) => cb(_user));
      return;
    }
    // No valid stored token — try to restore from va_session cookie before redirecting to /auth.
    // A 3-second timeout prevents stalling the app if the server is unreachable.
    fetch('/api/me/identity', { signal: AbortSignal.timeout(3000) })
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (data?.email) {
          _user = { sub: data.sub, email: data.email, name: data.name, picture: data.picture };
        }
      })
      .catch(() => {})
      .finally(() => {
        _authResolved = true;
        _listeners.forEach((cb) => cb(_user));
        offerOneTap();
      });
  };

  const tryInit = () => {
    if (typeof google !== 'undefined' && google.accounts?.id) {
      _gisReady = true;
      google.accounts.id.initialize({
        client_id: CLIENT_ID,
        callback: handleCredentialResponse,
        auto_select: true,
        cancel_on_tap_outside: false,
        use_fedcm_for_prompt: true, // FedCM One-Tap (mandatory since Aug 2025 / Chrome 149)
        use_fedcm_for_button: true, // FedCM active-mode for the rendered sign-in button
      });
      offerOneTap();
    } else {
      setTimeout(tryInit, 100);
    }
  };

  settleAuth();
  tryInit();
}

// ── Public API (matches firebase.config.ts exports) ──────────────────────────

/** Subscribe to auth state changes. Returns unsubscribe fn. */
export function onAuthChange(callback: (user: GoogleUser | null) => void): () => void {
  _listeners.add(callback);
  // Only fire immediately if the initial auth check has completed.
  // During init, the cookie restore fetch is in-flight — firing null prematurely
  // would redirect to /auth before the cookie check finishes, causing a loop.
  if (_authResolved) {
    setTimeout(() => callback(_user), 0);
  }
  return () => _listeners.delete(callback);
}

/** Trigger Google One Tap / sign-in prompt */
export async function signInWithGoogle(): Promise<void> {
  if (!_gisReady || typeof google === 'undefined') return;
  return new Promise((resolve) => {
    google.accounts.id.prompt(() => resolve());
  });
}

/** Sign out and clear stored token */
export async function signOut(): Promise<void> {
  if (_gisReady && typeof google !== 'undefined') {
    google.accounts.id.disableAutoSelect();
    if (_user?.email) {
      try { google.accounts.id.revoke(_user.email, () => {}); } catch { /* ignore */ }
    }
  }
  setUser(null, null);
}

/** Get a valid ID token (returns null if not authenticated or token expired) */
export async function getIdToken(): Promise<string | null> {
  if (_idToken) {
    const payload = decodeJwt(_idToken);
    if (payload && payload['exp'] * 1000 < Date.now()) {
      // Token expired — clear it. The va_session cookie carries auth from here;
      // the user re-authenticates via the button only after the cookie expires.
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
      _idToken = null;
    }
  }
  return _idToken;
}

/** Render a Google Sign-In button in the target element */
export function renderSignInButton(container: HTMLElement) {
  const btnOpts = {
    type: 'standard' as const,
    theme: 'filled_blue' as const,
    size: 'large' as const,
    text: 'sign_in_with' as const,
    shape: 'rectangular' as const,
    width: 280,
  };
  if (_gisReady && typeof google !== 'undefined') {
    google.accounts.id.renderButton(container, btnOpts);
  } else {
    // Wait for initGoogleAuth() to call initialize() first
    const tryRender = () => {
      if (_gisReady && typeof google !== 'undefined') {
        google.accounts.id.renderButton(container, btnOpts);
      } else {
        setTimeout(tryRender, 100);
      }
    };
    tryRender();
  }
}
